// src/modules/Onboarding/runtime/external/featureFlags.ts
//
// Manifest-configurable feature flags — resolved when OnboardingBuilder asks
// via its `feature-flag-callback` prop. The callback itself (resolveFeatureFlag)
// lives in useOnboardingBuilderState.ts; this file only declares flags whose
// value is derived from ctx (not from ABSmartly).
//
// Contract: `defineFeatureFlag({ description, type, resolve })` where
// `resolve: (ctx) => boolean | number`. The `type` tag constrains the
// `resolve` return type at compile time via `FlagTagFor<T>`.
//
// Adding a flag: append one entry below, run `npm run generate-runtime-manifest`.

import { EU_REGION_COUNTRIES_REGEX } from '@/helpers/constants'
import { useFwfHandoff } from '@/modules/Fwf/useFwfHandoff'

import { defineFeatureFlag, type FeatureFlagsMap } from '../types'

// `hide_age_selection_screen` удалён вместе с lptype-веткой (динамические лендинги):
// локального резолвера у ключа больше нет. Референсы в conditionsForShow конфигов живут
// дальше — true приходит через funnelFeatureFlag-override (age-лендинги, напр. age-main.ts,
// шаг 1 resolveFeatureFlag), а без него резолюция падает в ABsmartly-фоллбэк:
// treatment() несуществующего эксперимента → 0 → false (age-экран показывается).
export const featureFlags = {
	hide_age_selection_screen: defineFeatureFlag({
		description:
			'True when the age was already collected upstream: an `age_selection*` landing (`lptype`) or a FwF pre-funnel age gate.',
		type: 'boolean',
		// The FwF branch reads module state rather than ctx — the fact is set once at
		// /fwf/* bootstrap and lives until reload, so there is no store to hang it on.
		// See `modules/Fwf/useFwfHandoff.ts`.
		resolve: (ctx) =>
			['age_selection', 'age_selection_mature'].includes(ctx.locationStore.getQueryParam('lptype') ?? '') ||
			useFwfHandoff().isAgeCollected(),
	}),

	disable_weight_loss_boost: defineFeatureFlag({
		description: 'True outside the EU — weight-loss-boost upsell is EU-only.',
		type: 'boolean',
		resolve: (ctx) => !EU_REGION_COUNTRIES_REGEX.test(ctx.mainStore.getCountryCode),
	}),

	influencer_traffic: defineFeatureFlag({
		description:
			'True for affiliate / influencer campaign traffic — the cohort that gets age-matched flow imagery instead of the default illustrations.',
		type: 'boolean',
		// Matches the `main-walking` influencer landing override; survives the hand-off into
		// the gender funnel because `getQueryParam` falls back to the stored entry query.
		resolve: (ctx) => /influencer/.test(String(ctx.locationStore.getQueryParam('utm_source') ?? '').toLowerCase()),
	}),
	// DEV-34114: `hide_landing_screens` removed. OB-landing screens are now
	// UNCONDITIONAL — every DIRECT entry (bare alias, /ob/, deep slug, CRM) shows
	// them. Skipping the landing after a legacy-landing handoff or a cross-funnel
	// redirect is done POSITIONALLY (OnboardingView `skipLandingScreens` + the
	// builder's `resolveEntryUrl`/`firstNonLandingSlug`), not by a persisted flag —
	// so a reload is a fresh direct entry that re-shows the loader deterministically.
} satisfies FeatureFlagsMap
